import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add API key to all requests
api.interceptors.request.use((config) => {
  const apiKey = localStorage.getItem('admin_api_key');
  if (apiKey) {
    config.headers['X-API-Key'] = apiKey;
  }
  return config;
});

// Types
export interface Customer {
  id: string;
  email: string;
  name: string | null;
  paddleCustomerId: string | null;
  paddleSubscriptionId: string | null;
  subscriptionStatus: string;
  telegramUserId: string | null;
  telegramBotToken: string | null;
  telegramBotUsername: string | null;
  plan: string;
  instance: Instance | null;
  createdAt: string;
  updatedAt: string;
}

export interface Instance {
  id: string;
  customerId: string;
  serverId: string | null;
  status: string;
  containerId: string | null;
  containerHost: string | null;
  containerName: string | null;
  lastHealthCheck: string | null;
  healthStatus: string | null;
  lastError: string | null;
  createdAt: string;
  customer?: { id: string; email: string; name: string | null; plan: string };
  server?: { id: string; name: string; host: string };
}

export interface Server {
  id: string;
  name: string;
  host: string;
  port: number;
  username: string;
  maxInstances: number;
  currentInstances: number;
  totalMemoryMb: number | null;
  usedMemoryMb: number | null;
  totalDiskGb: number | null;
  usedDiskGb: number | null;
  status: string;
  lastHealthCheck: string | null;
  createdAt: string;
  _count?: { instances: number };
}

export interface EventLog {
  id: string;
  customerId: string | null;
  instanceId: string | null;
  serverId: string | null;
  event: string;
  data: any;
  createdAt: string;
}

export interface Stats {
  customers: { total: number; active: number };
  instances: { total: number; running: number; stopped: number };
  servers: { total: number; active: number };
  revenue: { mrr: number; recentPayments: number };
}

export interface ServerHealth {
  status: string;
  uptime?: string;
  loadAvg?: number[];
  totalMemoryMb?: number;
  usedMemoryMb?: number;
  totalDiskGb?: number;
  usedDiskGb?: number;
  runningContainers: number;
  clawdbotContainers: number;
}

// API functions
export const adminApi = {
  // Stats
  getStats: () => api.get<Stats>('/api/admin/stats').then(r => r.data),
  getDailyStats: (days = 30) => api.get(`/api/admin/stats/daily?days=${days}`).then(r => r.data),

  // Servers
  getServers: () => api.get<Server[]>('/api/admin/servers').then(r => r.data),
  getServer: (id: string) => api.get<Server>(`/api/admin/servers/${id}`).then(r => r.data),
  createServer: (data: Partial<Server>) => api.post<Server>('/api/admin/servers', data).then(r => r.data),
  updateServer: (id: string, data: Partial<Server>) => api.put<Server>(`/api/admin/servers/${id}`, data).then(r => r.data),
  deleteServer: (id: string) => api.delete(`/api/admin/servers/${id}`).then(r => r.data),
  checkServerHealth: (id: string) => api.get<ServerHealth>(`/api/admin/servers/${id}/health`).then(r => r.data),
  checkAllServersHealth: () => api.post('/api/admin/servers/health-check-all').then(r => r.data),

  // Instances
  getInstances: (params?: { status?: string; serverId?: string; limit?: number; offset?: number }) =>
    api.get<{ instances: Instance[]; total: number }>('/api/admin/instances', { params }).then(r => r.data),
  getInstance: (id: string) => api.get<Instance>(`/api/admin/instances/${id}`).then(r => r.data),
  getInstanceLogs: (id: string, lines = 100) =>
    api.get<{ logs: string }>(`/api/admin/instances/${id}/logs?lines=${lines}`).then(r => r.data),
  restartInstance: (id: string) => api.post(`/api/admin/instances/${id}/restart`).then(r => r.data),
  stopInstance: (id: string) => api.post(`/api/admin/instances/${id}/stop`).then(r => r.data),
  checkInstanceHealth: (id: string) => api.post(`/api/admin/instances/${id}/health-check`).then(r => r.data),

  // Customers
  getCustomers: (params?: { search?: string; status?: string; plan?: string; limit?: number; offset?: number }) =>
    api.get<{ customers: Customer[]; total: number }>('/api/admin/customers', { params }).then(r => r.data),
  getCustomer: (id: string) => api.get<{ customer: Customer; events: EventLog[] }>(`/api/admin/customers/${id}`).then(r => r.data),

  // Events
  getEvents: (params?: { event?: string; customerId?: string; serverId?: string; from?: string; to?: string; limit?: number; offset?: number }) =>
    api.get<{ events: EventLog[]; total: number }>('/api/admin/events', { params }).then(r => r.data),

  // API Keys
  getApiKeys: () => api.get('/api/admin/api-keys').then(r => r.data),
  createApiKey: (name: string, permissions?: string[]) => api.post('/api/admin/api-keys', { name, permissions }).then(r => r.data),
  deleteApiKey: (id: string) => api.delete(`/api/admin/api-keys/${id}`).then(r => r.data),

  // Export
  exportCustomers: () => window.open(`${API_URL}/api/admin/export/customers?api_key=${localStorage.getItem('admin_api_key')}`),
  exportEvents: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    params.set('api_key', localStorage.getItem('admin_api_key') || '');
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    window.open(`${API_URL}/api/admin/export/events?${params}`);
  },
};

export default api;
